import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertTriangle, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../hooks/useAuth';
import maintenanceService from '../../services/maintenanceService';
import UniformMaintenanceModal from '../../components/modals/UniformMaintenanceModal';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkMaintenance = async () => {
      const status = await maintenanceService.refreshMaintenanceStatus();
      // Statut de maintenance vérifié
      if (status.maintenanceType === 'partial') {
        setMaintenanceMessage('Site en maintenance. Revenez bientôt !');
        setShowMaintenanceModal(true);
      }
    };
    checkMaintenance();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Vérifier le mode maintenance avant de procéder
    if (!maintenanceService.isLoginAllowed()) {
      setShowMaintenanceModal(true);
      return;
    }

    if (!email || !password) {
      setPasswordError('Veuillez remplir tous les champs');
      return;
    }

    setIsLoading(true);
    setPasswordError(''); // Reset password error
    try {
      const result = await login(email, password);
      if (result.success) {
        if (result.requiresVerification) {
          // Pas de notification de succès
        } else {
          // Pas de notification de succès
          navigate('/dashboard');
        }
      }
    } catch (error: any) {
      // Afficher l'erreur localement au lieu d'une notification
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setPasswordError('Mot de passe incorrect');
      } else if (error.code === 'auth/user-not-found') {
        setPasswordError('Aucun compte trouvé avec cet email');
      } else {
        setPasswordError('Mot de passe incorrect');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="w-full max-w-sm"
      >
        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-3xl shadow-2xl shadow-gray-900/5 p-10">
          {/* Logo */}
          <div className="text-center mb-10">
            <div className="flex items-center justify-center mb-6">
              <Link to="/" className="transition-all duration-300 hover:scale-105 hover:opacity-80">
                <img 
                  src="/logo.png" 
                  alt="EspaceResto" 
                  className="h-12 w-auto cursor-pointer"
                />
              </Link>
            </div>
            <h1 className="text-2xl font-light text-gray-900 mb-3 tracking-tight">Connexion</h1>
            <p className="text-gray-500 font-light text-sm">Accédez à votre espace restaurant</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-7">
            <div className="space-y-1">
              <Input
                label="Adresse email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                icon={<Mail className="w-4 h-4" />}
                required
              />
            </div>

            <div className="space-y-1">
              <Input
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError(''); // Clear error when typing
                }}
                placeholder="••••••••"
                icon={<Lock className="w-4 h-4" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
                required
              />
              {passwordError && (
                <p className="text-xs text-red-500 mt-2 ml-1 font-light">{passwordError}</p>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900 focus:ring-2 transition-all duration-200"
                />
                <span className="ml-2 text-xs text-gray-500 group-hover:text-gray-700 transition-colors font-light">Se souvenir de moi</span>
              </label>
              <motion.button 
                type="button"
                whileHover={{ scale: 1.05 }}
                onClick={() => {/* Mot de passe oublié */}}
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200 font-light"
              >
                Mot de passe oublié ?
              </motion.button>
            </div>

            <div className="pt-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="submit"
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-4 rounded-2xl font-light transition-all duration-300 hover:shadow-lg hover:shadow-gray-900/20"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Connexion...
                    </div>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </motion.div>
            </div>
          </form>

          <div className="mt-10 text-center">
            <p className="text-xs text-gray-500 font-light">
              Pas encore de compte ?{' '}
              <motion.button 
                whileHover={{ scale: 1.05 }}
                onClick={() => navigate('/register')}
                className="text-gray-700 hover:text-gray-900 font-medium transition-colors duration-200"
              >
                Créer un compte
              </motion.button>
            </p>
          </div>
        </div>
      </motion.div>

      {/* Modal de maintenance */}
      <UniformMaintenanceModal
        isOpen={showMaintenanceModal}
        onClose={() => {
          setShowMaintenanceModal(false);
          navigate('/');
        }}
      />
    </div>
  );
};

export default Login;